/**
 * Configuration globale pour le système de scoring adaptatif
 * Ce fichier contient les paramètres de scoring (synchronisé avec config/environment.env)
 * 
 * ⚠️ IMPORTANT: Ces valeurs doivent être maintenues en sync avec config/environment.env
 * Pour modifier ces valeurs, éditez config/environment.env et reportez les changements ici
 */

export const SCORING_CONFIG = {
  /**
   * Seuil unique (sur 10) : validation QCM, message Excellent, streaks, jauge, etc.
   * Variantes par examen : getValidationThresholdForExam.
   */
  validation: {
    qcm_validation_threshold: 7.0
  },
  scoring: {
    value_correct_answer: 2,
    value_distractor: -1.5,
    expected_time_seconds: 300
  },
  /** Utilisés pour stats / autres features — ne multiplient plus le score (voir calculs unitaires / exam). */
  certainty_coefficients: {
    high: 1.0,
    medium: 0.75,
    low: 0.4,
    default: 0.75
  },
  speed_coefficients: {
    fast: { threshold_percentage: 15, coefficient: 1.0, label: 'Fast' },
    normal: { threshold_percentage: 90, coefficient: 0.95, label: 'Normal' },
    limit: { threshold_percentage: 100, coefficient: 0.9, label: 'Limit' },
    slow: { threshold_percentage: 130, coefficient: 0.75, label: 'Slow' },
    very_slow: { threshold_percentage: 10000, coefficient: 0.6, label: 'Very Slow' }
  },
  speed_progressive_curve: {
    enabled: true,
    min_percentage: 15,
    max_percentage: 130,
    min_coefficient: 1.0,
    max_coefficient: 0.6
  },
  score_levels: {
    beginner: { max_score: 1, label: 'Débutant' },
    learning: { max_score: 5, label: 'En apprentissage' },
    competent: { max_score: 6.5, label: 'Compétent' },
    advanced: { max_score: 8, label: 'Avancé' },
    expert: { max_score: 100, label: 'Expert' }
  },
  consistency: {
    excellent_threshold: 1.5,
    good_threshold: 2.5,
    variable_threshold: 4.0,
    labels: {
      excellent: 'Excellent',
      good: 'Good',
      variable: 'Variable',
      unstable: 'Unstable'
    }
  },
  streaks: {
    enabled: true
  }
};

/**
 * Helpers pour accéder facilement aux valeurs de configuration
 */
export const getScoringConfig = () => SCORING_CONFIG;

export const getValidationThreshold = () => SCORING_CONFIG.validation.qcm_validation_threshold;

/**
 * Retourne le seuil de validation spécifique à un examen (score sur 10)
 * @param {string} examId - ID de l'examen (ex: 'MLS-C01_V4')
 * @returns {number} Seuil de validation (sur échelle 0-10)
 */
export const getValidationThresholdForExam = (examId) => {
    if (!examId) {
        return getValidationThreshold();
    }
    
    // Seuils spécifiques par examen (synchronisés avec backend/config/scoring_config.py)
    const EXAM_VALIDATION_THRESHOLDS = {
        'MLS-C01_V4': 7.0,
        // Ajouter d'autres examens ici si nécessaire
    };
    
    return EXAM_VALIDATION_THRESHOLDS[examId] || getValidationThreshold();
};

export const getCertaintyCoeff = (certaintyLevel) => {
    return SCORING_CONFIG.certainty_coefficients[certaintyLevel] || SCORING_CONFIG.certainty_coefficients.default;
};

/**
 * Calcule le coefficient de vitesse selon la configuration
 * @param {number} speedPercentage - Pourcentage du temps utilisé (0-infinity)
 * @returns {number} Coefficient de vitesse
 */
export const getSpeedCoeff = (speedPercentage) => {
    const config = SCORING_CONFIG.speed_progressive_curve;
    
    if (config.enabled) {
        if (speedPercentage <= config.min_percentage) {
            return config.min_coefficient;
        }
        if (speedPercentage >= config.max_percentage) {
            return config.max_coefficient;
        }
        
        const slope = (config.max_coefficient - config.min_coefficient) / 
                     (config.max_percentage - config.min_percentage);
        return config.min_coefficient + slope * (speedPercentage - config.min_percentage);
    } else {
        const speedCoeffs = SCORING_CONFIG.speed_coefficients;
        if (speedPercentage <= speedCoeffs.fast.threshold_percentage) {
            return speedCoeffs.fast.coefficient;
        } else if (speedPercentage < speedCoeffs.normal.threshold_percentage) {
            return speedCoeffs.normal.coefficient;
        } else if (speedPercentage <= speedCoeffs.limit.threshold_percentage) {
            return speedCoeffs.limit.coefficient;
        } else if (speedPercentage <= speedCoeffs.slow.threshold_percentage) {
            return speedCoeffs.slow.coefficient;
        } else {
            return speedCoeffs.very_slow.coefficient;
        }
    }
};

/**
 * Retourne le label de vitesse selon le pourcentage
 */
export const getSpeedLabel = (speedPercentage) => {
    const speedCoeffs = SCORING_CONFIG.speed_coefficients;
    if (speedPercentage <= speedCoeffs.fast.threshold_percentage) {
        return speedCoeffs.fast.label;
    } else if (speedPercentage < speedCoeffs.normal.threshold_percentage) {
        return speedCoeffs.normal.label;
    } else if (speedPercentage <= speedCoeffs.limit.threshold_percentage) {
        return speedCoeffs.limit.label;
    } else if (speedPercentage <= speedCoeffs.slow.threshold_percentage) {
        return speedCoeffs.slow.label;
    } else {
        return speedCoeffs.very_slow.label;
    }
};

/**
 * Retourne le niveau de maîtrise selon le score
 */
export const getScoreLevel = (score) => {
    const levels = SCORING_CONFIG.score_levels;
    if (score < levels.beginner.max_score) {
        return levels.beginner;
    } else if (score < levels.learning.max_score) {
        return levels.learning;
    } else if (score < levels.competent.max_score) {
        return levels.competent;
    } else if (score < levels.advanced.max_score) {
        return levels.advanced;
    } else {
        return levels.expert;
    }
};

/**
 * Calcule la métrique de consistance (régularité) des scores
 * @param {number[]} scores - Tableau de scores
 * @returns {Object} {variance, stdDev, label}
 */
export const calculateConsistency = (scores) => {
    if (!scores || scores.length < 2) {
        return { variance: 0, stdDev: 0, label: 'N/A' };
    }
    
    const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    const variance = scores.reduce((sum, score) => sum + Math.pow(score - mean, 2), 0) / scores.length;
    const stdDev = Math.sqrt(variance);
    
    const config = SCORING_CONFIG.consistency;
    let label = config.labels.unstable;
    
    if (stdDev < config.excellent_threshold) {
        label = config.labels.excellent;
    } else if (stdDev < config.good_threshold) {
        label = config.labels.good;
    } else if (stdDev < config.variable_threshold) {
        label = config.labels.variable;
    }
    
    return { variance, stdDev, label };
};

/**
 * Calcule le streak (série de réussites consécutives)
 * @param {number[]} scores - Tableau de scores (du plus récent au plus ancien)
 * @returns {number} Nombre de QCM validés consécutivement
 */
export const calculateStreak = (scores) => {
    if (!SCORING_CONFIG.streaks.enabled || !scores || scores.length === 0) {
        return 0;
    }
    
    const threshold = getValidationThreshold();
    let streak = 0;
    
    for (let i = 0; i < scores.length; i++) {
        if (scores[i] >= threshold) {
            streak++;
        } else {
            break;
        }
    }
    
    return streak;
};

export default SCORING_CONFIG;


